import { formatDateWithTime } from '@longy/common'
import { Button, Skeleton } from '@longy/web-components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { CatalogCard, CatalogPage } from '../../components/catalog/CatalogPage'
import { WebinarCtaButton } from '../../components/webinars/WebinarCtaButton'
import { api } from '../../core/trpc'
import { useAuth } from '../../hooks/useAuth'
import { useLocale } from '../../hooks/useLocale'
import { formatPrice } from '../../utils/formatPrice'

const PATH = '/webinars/'

const WebinarsPage = () => {
    const { t } = useTranslation()
    const { getLocalizedValue } = useLocale()

    const { isAuthenticated } = useAuth()

    const { data: webinars, isPending } = api.webinar.listPublicUpcoming.useQuery({})

    /**
     * A webinar seats 25, so a session somebody already booked stays listed and bookable — unlike
     * a consultation, whose single seat takes it off the calendar. The card says so rather than
     * letting them reach a checkout that refuses them. Signed-out visitors browse this catalogue,
     * so it only asks once there is somebody to ask about.
     */
    const { data: bookedItemIds } = api.appointment.getBookedSlotItemIds.useQuery(undefined, {
        enabled: isAuthenticated,
    })
    const booked = new Set(bookedItemIds ?? [])

    return (
        <CatalogPage
            title={t('frontend.catalog.webinarsTitle')}
            lead={t('frontend.catalog.webinarsLead')}
        >
            {isPending ? (
                <Skeleton className="h-64 w-full" />
            ) : !webinars?.length ? (
                <p className="text-brand-gray">{t('frontend.catalog.nothingAvailable')}</p>
            ) : (
                <ul className="grid gap-4 sm:grid-cols-2">
                    {webinars.map((webinar) => (
                        <li key={webinar.purchasableItemId} className="flex">
                            <CatalogCard
                                title={getLocalizedValue(webinar.name)}
                                subtitle={webinar.expertName}
                                meta={
                                    <div className="flex flex-col gap-1 text-sm">
                                        {webinar.startDate && (
                                            <span className="text-brand-gray">
                                                {formatDateWithTime(webinar.startDate)}
                                            </span>
                                        )}
                                        {webinar.listPrice != null && (
                                            <span className="font-semibold text-primary">
                                                {formatPrice(webinar.listPrice)}
                                            </span>
                                        )}
                                        <span className="text-brand-gray">
                                            {webinar.freePlaces > 0
                                                ? `${t('frontend.catalog.freePlaces')}: ${webinar.freePlaces}`
                                                : t('frontend.catalog.soldOut')}
                                        </span>
                                    </div>
                                }
                                action={
                                    <div className="flex flex-wrap gap-2">
                                        <WebinarCtaButton
                                            purchasableItemId={webinar.purchasableItemId}
                                            isBooked={booked.has(webinar.purchasableItemId)}
                                            freePlaces={webinar.freePlaces}
                                        />
                                        <Button asChild size="sm" variant="outline">
                                            <Link
                                                to="/webinars/$webinarId"
                                                params={{ webinarId: webinar.purchasableItemId }}
                                            >
                                                {t('frontend.catalog.details')}
                                            </Link>
                                        </Button>
                                    </div>
                                }
                            />
                        </li>
                    ))}
                </ul>
            )}
        </CatalogPage>
    )
}

export const Route = createFileRoute(PATH)({
    head: () => ({ meta: [{ title: 'Long-Y - Webináriumok' }] }),
    loader: async ({ context }) => {
        await context.apiUtils.webinar.listPublicUpcoming.ensureData({}).catch(() => undefined)
    },
    component: WebinarsPage,
})
